import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, Bookmark, Download, ArrowLeft, PlayCircle, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PublicShell, Loading, TierBadge, ResourceCard } from "@/components/shared";
import { TIERS } from "@/lib/constants";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ResourceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["resource", slug],
    queryFn: async () => (await api.get(`/resources/${slug}`)).data,
  });

  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  if (!data) return <PublicShell><div className="py-24 text-center text-slate-500">Resource not found.</div></PublicShell>;

  const toggleSave = async () => {
    if (!user) return navigate("/login");
    try { const res = await api.post(`/resources/${data.id}/save`); toast.success(res.data.saved ? "Saved" : "Removed"); refetch(); }
    catch { toast.error("Could not save"); }
  };
  const download = async () => {
    if (!user) return navigate("/login");
    try { await api.post(`/resources/${data.id}/download`); toast.success("Download started (demo files)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Upgrade required"); }
  };

  return (
    <PublicShell>
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-5 py-8 lg:px-8">
          <button onClick={() => navigate("/resources")} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-navy" data-testid="resource-back"><ArrowLeft className="h-4 w-4" /> Back to library</button>
          <div className="grid gap-8 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                <span>{data.category}</span><span>·</span><span>{data.business_stage}</span><span>·</span><span>{data.resource_format}</span>
              </div>
              <h1 className="font-heading text-3xl font-bold tracking-tight text-navy sm:text-4xl">{data.title}</h1>
              <p className="mt-3 text-lg text-slate-600">{data.short_description}</p>
              <div className="mt-5 flex items-center gap-3">
                <TierBadge tier={data.membership_requirement} />
                {user && <Button variant="outline" size="sm" onClick={toggleSave} data-testid="resource-save-btn" className="rounded-md"><Bookmark className={`mr-1.5 h-4 w-4 ${data.saved ? "fill-emerald text-emerald" : ""}`} /> {data.saved ? "Saved" : "Save"}</Button>}
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="overflow-hidden rounded-xl border border-slate-200">
                {data.cover_image ? <img src={data.cover_image} alt={data.title} className="aspect-video w-full object-cover" /> : <div className="aspect-video bg-navy" />}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-10 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <h2 className="font-heading text-xl font-semibold text-navy">Overview</h2>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-slate-700">{data.full_description}</p>

            {data.locked ? (
              <div className="mt-8 rounded-xl border border-emerald/30 bg-emerald/5 p-6" data-testid="resource-locked">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-emerald/10 text-emerald"><Lock className="h-5 w-5" /></div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">This resource needs the {TIERS[data.membership_requirement]?.label} plan</h3>
                <p className="mt-2 text-sm text-slate-600">Unlock the full download, implementation steps and completed example — plus the entire library.</p>
                <Button onClick={() => navigate("/pricing")} className="mt-4 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="resource-upgrade-btn">See plans</Button>
              </div>
            ) : (
              <>
                {data.implementation_instructions && (
                  <div className="mt-8">
                    <h2 className="font-heading text-xl font-semibold text-navy">How to implement</h2>
                    <p className="mt-3 whitespace-pre-line leading-relaxed text-slate-700">{data.implementation_instructions}</p>
                  </div>
                )}
                {data.completed_example && (
                  <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
                    <div className="flex items-center gap-2 text-emerald"><CheckCircle2 className="h-5 w-5" /><span className="font-heading font-semibold">Completed example</span></div>
                    <p className="mt-2 text-sm text-slate-600">{data.completed_example}</p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="font-heading font-semibold text-navy">Get this resource</h3>
                {!data.locked && data.files?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {data.files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600"><FileText className="h-4 w-4 text-slate-400" /> {f.name}</div>
                    ))}
                  </div>
                )}
                <Button onClick={data.locked ? () => navigate("/pricing") : download} className="mt-4 w-full rounded-md bg-navy text-white hover:bg-navysurface" data-testid="resource-download-btn">
                  {data.locked ? <><Lock className="mr-2 h-4 w-4" /> Unlock to download</> : <><Download className="mr-2 h-4 w-4" /> Download</>}
                </Button>
              </div>
              {data.video_url && !data.locked && (
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center gap-2 text-navy"><PlayCircle className="h-5 w-5 text-emerald" /><span className="font-heading font-semibold">Video walkthrough</span></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {data.related_resources?.length > 0 && (
          <div className="mt-14">
            <h2 className="font-heading text-xl font-semibold text-navy">Related resources</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{data.related_resources.map((r) => <ResourceCard key={r.id} r={r} />)}</div>
          </div>
        )}
      </section>
    </PublicShell>
  );
}
